import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useInterlinearOverlay — fetches the KJV+Strong's per-verse overlay for the
 * currently-loaded chapter and the matching Strong's lexicon, with an in-memory
 * Map cache keyed by `${book}-${chapter}` so toggling between chapters reuses
 * already-fetched overlays without refetching.
 *
 * @param {boolean} interlinearOn  whether the toggle is currently on
 * @param {string}  book           selected book name
 * @param {number}  chapter        selected chapter number
 *
 * @returns {{
 *   taggedByVerse: Record<number,string>,
 *   lexicon: Record<string,object>,
 *   loading: boolean
 * }}
 */
export default function useInterlinearOverlay(interlinearOn, book, chapter, strongsTranslation = "KJV+Strong's") {
  const overlayCacheRef = useRef(new Map()); // key: "Book-Chapter-Tr" → { [verseNo]: taggedText }
  const lexiconCacheRef = useRef(new Map());
  const [taggedByVerse, setTaggedByVerse] = useState({});
  const [lexicon, setLexicon] = useState({});
  const [loading, setLoading] = useState(false);

  // When the chapter changes (or the toggle flips off), surface the cached
  // value for the new chapter (or empty objects) without refetching.
  useEffect(() => {
    const key = `${book}-${chapter}-${strongsTranslation}`;
    setTaggedByVerse(overlayCacheRef.current.get(key) || {});
    setLexicon(lexiconCacheRef.current.get(key) || {});
  }, [book, chapter, strongsTranslation]);

  // Fetch overlay (Strong's-tagged chapter) on demand.
  useEffect(() => {
    if (!interlinearOn) return;
    const key = `${book}-${chapter}-${strongsTranslation}`;
    if (overlayCacheRef.current.has(key)) return; // cache hit

    let cancelled = false;
    setLoading(true);
    api
      .getChapter(book, chapter, strongsTranslation)
      .then((resp) => {
        if (cancelled) return;
        const map = {};
        (resp?.verses || []).forEach((v) => {
          if (v.has_strongs && v.text) map[v.verse] = v.text;
        });
        overlayCacheRef.current.set(key, map);
        setTaggedByVerse(map);
      })
      .catch((err) => console.warn("Strong's overlay fetch failed:", err))
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [interlinearOn, book, chapter, strongsTranslation]);

  // Fetch matching lexicon entries once the overlay text is available.
  useEffect(() => {
    if (!interlinearOn) return;
    const key = `${book}-${chapter}-${strongsTranslation}`;
    const cachedLex = lexiconCacheRef.current.get(key) || {};
    const taggedTexts = Object.values(taggedByVerse);
    if (taggedTexts.length === 0) return;

    const idSet = new Set();
    const re = /<w\s+s=['"]([HG]\d+)['"]>/g;
    taggedTexts.forEach((t) => {
      let m;
      re.lastIndex = 0;
      while ((m = re.exec(t)) !== null) idSet.add(m[1]);
    });
    const missing = [...idSet].filter((id) => !cachedLex[id]);
    if (missing.length === 0) {
      setLexicon(cachedLex);
      return;
    }

    let cancelled = false;
    setLoading(true);
    axios
      .post(`${BACKEND_URL}/api/strongs/batch`, { strong_ids: missing })
      .then((r) => {
        if (cancelled) return;
        const fetched = r.data?.entries || {};
        const merged = { ...cachedLex, ...fetched };
        lexiconCacheRef.current.set(key, merged);
        setLexicon(merged);
      })
      .catch((err) => console.warn('Interlinear batch fetch failed:', err))
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [interlinearOn, book, chapter, strongsTranslation, taggedByVerse]);

  return { taggedByVerse, lexicon, loading };
}
